/**
 * Tabellone degli esiti: il sistema aveva ragione? (SPEC-023, F-126)
 *
 * ## Tre filtri e **nessuna configurazione**
 *
 * Periodo, specie, orizzonte. Non c'è nulla da impostare, ed è una scelta: ogni
 * parametro tarabile sarebbe un invito a tarare i numeri finché non dicono la
 * cosa desiderata. La feature misura e basta.
 *
 * ## L'onestà statistica è la feature, non una nota a piè di pagina
 *
 *  1. **`n` accanto a ogni percentuale, sempre.** Nessun tooltip: accanto.
 *  2. **Sotto la soglia la percentuale non si mostra.** Non in grigio, non con
 *     un asterisco: al suo posto «14 casi — troppo pochi per una percentuale».
 *     Una percentuale mostrata viene letta, qualunque avvertenza le stia intorno.
 *  3. **L'intervallo di confidenza accanto al numero.** «68% (n=24, 48–83%)»
 *     dice da solo ciò che tre righe di avvertenze non ottengono.
 *
 * ⚠️ **Questi numeri non retroagiscono su nulla**: nessun prompt, nessun
 * cancello, nessuna soglia li legge. Correggere una decisione automatica su una
 * statistica non significativa è il modo più rapido di inseguire il rumore con
 * denaro vero — ed è scritto in pagina, non solo qui.
 */

import { useState } from 'react';
import { ApiError } from '@/lib/api-client';
import { formatDate, formatNumber, formatPercent } from '@/lib/format';
import { Alert, Button, Card, EmptyState, Select, Spinner, StatCard, TableWrap } from '@/ui';
import {
  downloadExport,
  useRecomputeOutcomes,
  useScoreboard,
  type Accuracy,
  type OutcomeSpecies,
  type ScoreRow,
} from './api';
import { FlowProfileSection } from './FlowProfileSection';
import './outcomes.css';

const SPECIES_LABELS: Record<OutcomeSpecies, string> = {
  instrument_analysis: 'Analisi di dettaglio',
  daily_briefing_item: 'Digest di portafoglio',
  daily_pick_result: 'Titolo del giorno',
  auto_trade_proposal: 'Verifica di auto-trading',
  value_trigger: 'Scatto di valore',
};

const PERIODS = [
  { value: '30', label: 'Ultimi 30 giorni' },
  { value: '90', label: 'Ultimi 90 giorni' },
  { value: '180', label: 'Ultimi 6 mesi' },
  { value: '365', label: 'Ultimo anno' },
];

const HORIZONS = [
  { value: '7', label: '7 giorni' },
  { value: '30', label: '30 giorni' },
  { value: '90', label: '90 giorni' },
];

function labelOf(key: string): string {
  return SPECIES_LABELS[key as OutcomeSpecies] ?? key;
}

/**
 * La percentuale, o il motivo per cui non c'è.
 *
 * ⚠️ Sotto soglia **non** si scrive un numero in grigio: si scrive il
 * conteggio. Una percentuale mostrata viene letta, qualunque avvertenza le stia
 * intorno — e questo componente è il punto in cui quella regola vive o muore.
 */
export function AccuracyCell({ accuracy, minSample }: { accuracy: Accuracy; minSample: number }) {
  if (accuracy.scored === 0) return <span className="cell--minor">nessun caso</span>;
  if (accuracy.pct === null) {
    return (
      <span className="cell--minor">
        {formatNumber(accuracy.scored)} casi — troppo pochi per una percentuale (ne servono {minSample})
      </span>
    );
  }
  return (
    <span className="outcomes__accuracy">
      <strong>{accuracy.pct}%</strong>
      <span className="cell--minor">
        {' '}
        (n={formatNumber(accuracy.scored)}, {accuracy.lowPct}–{accuracy.highPct}%)
      </span>
    </span>
  );
}

function ScoreTable({ rows, header, minSample }: { rows: ScoreRow[]; header: string; minSample: number }) {
  if (rows.length === 0) {
    return <p className="cell--minor">Nessun giudizio misurato in questo periodo.</p>;
  }
  return (
    <TableWrap>
      <table className="table table--compact">
        <thead>
          <tr>
            <th>{header}</th>
            <th>Accuratezza</th>
            <th className="numeric">Azzeccati</th>
            <th className="numeric">Sbagliati</th>
            <th className="numeric col--secondary">Neutri</th>
            <th className="numeric col--secondary">Non misurati</th>
            <th className="numeric col--tertiary">Errore mediano sull obiettivo</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.key}>
              <td>{labelOf(row.key)}</td>
              <td>
                <AccuracyCell accuracy={row.accuracy} minSample={minSample} />
              </td>
              <td className="numeric">{formatNumber(row.hits)}</td>
              <td className="numeric">{formatNumber(row.misses)}</td>
              <td className="numeric col--secondary">{formatNumber(row.neutral)}</td>
              <td className="numeric col--secondary">
                {row.unmeasurable > 0 ? formatNumber(row.unmeasurable) : '—'}
              </td>
              <td className="numeric col--tertiary">
                {row.medianTargetErrorPct === null ? (
                  <span className="cell--minor">—</span>
                ) : (
                  <>
                    {formatPercent(row.medianTargetErrorPct)}
                    <span className="cell--minor"> (n={formatNumber(row.targetErrorCount)})</span>
                  </>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </TableWrap>
  );
}

/**
 * Le due domande gemelle sullo stesso periodo.
 *
 * ⚠️ Due sezioni e non due pagine: condividono il filtro, la disciplina dei
 * «non misurati» e la sorgente dei costi — e la barra laterale ha gia' sedici
 * voci, una delle quali e' costata fatica a liberare (F-090).
 */
type Tab = 'esiti' | 'flussi';

export function OutcomesPage() {
  const [tab, setTab] = useState<Tab>('esiti');
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [days, setDays] = useState(90);
  const [horizon, setHorizon] = useState(30);
  const [species, setSpecies] = useState<OutcomeSpecies | 'all'>('all');
  const board = useScoreboard(days, horizon, species);
  const recompute = useRecomputeOutcomes();

  const scarica = (formato: 'csv') => {
    setExporting(true);
    setExportError(null);
    const path =
      tab === 'flussi'
        ? `/outcomes/flow?days=${days}&format=${formato}`
        : `/outcomes?days=${days}&horizon=${horizon}${species === 'all' ? '' : `&subjectType=${species}`}&format=${formato}`;
    downloadExport(path, `labinvestment-${tab}.${formato}`)
      .catch((error: unknown) => {
        // ⚠️ Un export fallito si dice: un clic che non produce un file e non
        // dice niente si legge come «il pulsante non funziona».
        setExportError(error instanceof Error ? error.message : 'Export non riuscito');
      })
      .finally(() => setExporting(false));
  };

  if (board.isPending) return <Spinner label="Carico gli esiti…" />;
  if (board.error) {
    return (
      <Alert variant="error" title="Esiti non caricati">
        {board.error instanceof ApiError ? board.error.message : 'Errore imprevisto.'}
      </Alert>
    );
  }

  const data = board.data;
  const speciesOptions = [
    { value: 'all', label: 'Tutte le specie' },
    ...data.species.map((key) => ({ value: key, label: SPECIES_LABELS[key] })),
  ];

  return (
    <>
      <div className="outcomes__tabs" role="group" aria-label="Cosa guardare">
        <button
          type="button"
          className={`outcomes__tab${tab === 'esiti' ? ' is-active' : ''}`}
          aria-pressed={tab === 'esiti'}
          onClick={() => setTab('esiti')}
        >
          Il sistema aveva ragione?
        </button>
        <button
          type="button"
          className={`outcomes__tab${tab === 'flussi' ? ' is-active' : ''}`}
          aria-pressed={tab === 'flussi'}
          onClick={() => setTab('flussi')}
        >
          Dove si perde?
        </button>
      </div>

      <Card
        title="Cosa si sta guardando"
        subtitle={`Giudizi emessi fra il ${formatDate(data.from)} e il ${formatDate(data.to)}, misurati a ${data.horizon} giorni`}
        action={
          <Button
            variant="ghost"
            onClick={() => recompute.mutate()}
            loading={recompute.isPending}
            title="Ricalcola: è aritmetica su dati già pagati, nessuna chiamata a pagamento"
          >
            Ricalcola
          </Button>
        }
      >
        <div className="outcomes__filters">
          <Select
            label="Periodo"
            value={String(days)}
            onChange={(event) => setDays(Number(event.target.value))}
            options={PERIODS}
          />
          <Select
            label="Orizzonte"
            value={String(horizon)}
            onChange={(event) => setHorizon(Number(event.target.value))}
            options={HORIZONS}
            hint="A 7 giorni si misura la settimana, a 90 se la tesi ha tenuto: domande diverse."
          />
          <Select
            label="Specie di giudizio"
            value={species}
            onChange={(event) => setSpecies(event.target.value as OutcomeSpecies | 'all')}
            options={speciesOptions}
          />
        </div>

        {/* ⚠️ L'export porta fuori ESATTAMENTE cio' che si sta guardando, filtri
            compresi: un file che ignorasse i filtri sarebbe una seconda verita',
            e quella su cui ci si fiderebbe di piu' e' sempre il file. */}
        <div className="outcomes__exports">
          <Button variant="ghost" onClick={() => scarica('csv')} loading={exporting}>
            Esporta CSV
          </Button>
          <a
            className="btn btn--ghost"
            href={`/api/outcomes${tab === 'flussi' ? '/flow' : ''}?days=${days}${tab === 'flussi' ? '' : `&horizon=${horizon}`}`}
            target="_blank"
            rel="noreferrer"
          >
            Vedi JSON
          </a>
          <span className="cell--minor">
            Il file porta il periodo e i filtri correnti, e le percentuali sotto soglia restano vuote.
          </span>
        </div>
        {exportError && (
          <Alert variant="error" title="Export non riuscito">
            {exportError}
          </Alert>
        )}
      </Card>

      {tab === 'flussi' && <FlowProfileSection days={days} />}

      {tab === 'esiti' && (data.totals.measured === 0 && data.totals.unmeasurable === 0 ? (
        <EmptyState
          icon="📭"
          title="Nessun esito ancora misurato"
          description="Il giro quotidiano misura i giudizi quando il loro orizzonte è maturato. Con «Ricalcola» si può forzare subito: non costa nulla, è aritmetica su dati già pagati."
        />
      ) : (
        <>
          <div className="outcomes__stats">
            <StatCard
              label="Accuratezza complessiva"
              value={
                data.totals.accuracy.pct === null
                  ? `${formatNumber(data.totals.accuracy.scored)} casi`
                  : `${data.totals.accuracy.pct}%`
              }
              hint={
                data.totals.accuracy.pct === null
                  ? `Troppo pochi per una percentuale: ne servono ${data.minSample}`
                  : `n=${formatNumber(data.totals.accuracy.scored)}, intervallo ${data.totals.accuracy.lowPct}–${data.totals.accuracy.highPct}%`
              }
            />
            <StatCard label="Azzeccati" value={formatNumber(data.totals.hits)} tone="positive" />
            <StatCard label="Sbagliati" value={formatNumber(data.totals.misses)} tone="negative" />
            <StatCard
              label="Neutri"
              value={formatNumber(data.totals.neutral)}
              hint="Dentro la banda neutra, oppure Hold: non sono scommesse e non si punteggiano"
            />
            <StatCard
              label="Non misurabili"
              value={formatNumber(data.totals.unmeasurable)}
              hint="Prezzo mancante o variazione implausibile: dichiarati, mai stimati"
              tone={data.totals.unmeasurable > 0 ? 'negative' : 'neutral'}
            />
          </div>

          {!data.rankable.species && !data.rankable.model && (
            <Alert variant="info" title="Nessuna classifica, per ora">
              Le righe qui sotto sono in ordine di <strong>numerosità</strong>, non di accuratezza. Ordinare
              per accuratezza righe che non hanno abbastanza casi significherebbe inventare una gara e poi
              crederci: l&apos;ordinamento si abilita quando tutte le righe confrontate superano i{' '}
              {data.minSample} casi.
            </Alert>
          )}

          <Card title="Per specie di giudizio" subtitle="Chi sbaglia, e su cosa." flush>
            <ScoreTable rows={data.bySpecies} header="Specie" minSample={data.minSample} />
          </Card>

          <Card
            title="Per modello"
            subtitle="Il modello che ha emesso il giudizio, come era allora: un documento rigenerato non riscrive la storia."
            flush
          >
            <ScoreTable rows={data.byModel} header="Modello" minSample={data.minSample} />
          </Card>

          <Card
            title="Per orizzonte"
            subtitle="Sempre tutti e tre, anche quando sopra se ne sta guardando uno: confrontarli è il motivo per cui se ne misurano tre."
            flush
          >
            <ScoreTable rows={data.byHorizon} header="Orizzonte (giorni)" minSample={data.minSample} />
          </Card>

          <Card title="Quanto è costato" subtitle={`In dollari, nel periodo selezionato.`} flush>
            <TableWrap>
              <table className="table table--compact">
                <thead>
                  <tr>
                    <th>Specie</th>
                    <th className="numeric">Costo</th>
                    <th className="numeric col--secondary">Chiamate</th>
                    <th className="numeric">Per giudizio azzeccato</th>
                  </tr>
                </thead>
                <tbody>
                  {data.cost.bySpecies.map((row) => (
                    <tr key={row.subjectType}>
                      <td>
                        {SPECIES_LABELS[row.subjectType]}
                        {row.note && <div className="cell--minor">{row.note}</div>}
                      </td>
                      <td className="numeric">{row.cost === null ? '—' : `$ ${Number(row.cost).toFixed(4)}`}</td>
                      <td className="numeric col--secondary">
                        {formatNumber(row.calls)}
                        {(row.estimated > 0 || row.unknown > 0) && (
                          <div className="cell--minor">
                            {row.estimated > 0 && `${formatNumber(row.estimated)} stimate`}
                            {row.estimated > 0 && row.unknown > 0 && ' · '}
                            {row.unknown > 0 && `${formatNumber(row.unknown)} senza prezzo`}
                          </div>
                        )}
                      </td>
                      <td className="numeric">
                        {row.costPerHit === null ? (
                          <span className="cell--minor">
                            {row.cost === null ? '—' : `sotto i ${data.minSample} casi`}
                          </span>
                        ) : (
                          `$ ${Number(row.costPerHit).toFixed(4)}`
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableWrap>
          </Card>
        </>
      ))}

      {/* ⚠️ Va detto in pagina e non solo nel codice: e' la difesa contro l'uso
          sbagliato piu' probabile di questa schermata. */}
      <p className="outcomes__disclaimer">
        Questi numeri <strong>non retroagiscono su nulla</strong>: nessun prompt, nessun cancello e nessuna
        soglia operativa li legge. Servono a guardare, non a correggere automaticamente — inseguire una
        statistica non significativa con denaro vero è il modo più rapido di inseguire il rumore.
      </p>
    </>
  );
}
